import NumberFormat from "react-number-format";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import React, {ReactNode} from "react";
import {convertLowerCase, doesCurrentTaxYearMatchCurrentYear, getCurrentTaxYear, trimSpace} from "./Utils";
import {currentMonthLastDate, maxYear, minYear, nextMonthLastDate} from "../constants";
import {formatValueAsDate} from "./DateUtils";
import moment from "moment";
import DatePicker from "react-date-picker";
import InputField from "../../components/common/form/InputField";
import SelectField from "../../components/common/form/SelectField";
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import {updateSummaryRowInfo} from "../../features/order/editor/TaxCertificate/TaxDetailTabSection";
import {dispatchUtilityRequest} from "../../app/utilityRequestsApi";

interface DetailRowProps {
    label?: string;
    value?: string;
    isValueSpan?: boolean;
    className?: string;
    isLoading?: boolean;
    isError?: boolean;
    title?: string;
    overrideValue?: ReactNode;
}

export const DetailRow = ({
                              label = "",
                              value = "",
                              isValueSpan = false,
                              className = "",
                              isLoading = false,
                              isError = false,
                              title = "",
                              overrideValue = null
                          }: DetailRowProps) => (
    <div className={`col-span-12 grid grid-cols-12 mb-3.5`}>
        <div className="col-span-5">
            <div className="text-surface-0 font-semibold text-[14px]">{label}</div>
        </div>
        <div className="col-span-7">
            {
                (isLoading || isError) ? <SkeletonLoader count={1}/> :
                    isValueSpan ?
                        <span className={`bg-highlight-4 text-text-0 text-[12px] p-1 rounded-md ${className}`}
                              title={title}>{value}
                        </span>
                        :
                        (overrideValue != null ? overrideValue :
                            <div className={`text-surface-3 text-[16px] ${className}`}
                                 title={title}>{value || "-"}</div>)
            }
        </div>
    </div>
);

export const Separator = () => {
    return <div className="flex gap-2 items-center justify-between border-b border-surface-3 pb-2"/>
}

export const getFullPersonName = (person: {
    firstName: string,
    middleName: string,
    lastName: string,
    nameUnparsed: string
}) => {
    return [person.firstName, person.middleName, person.lastName, person.nameUnparsed].join(' ');
}

export function setTaskList(taskList: any, setVendorTaskList: any, properties: any) {
    const updatedTaskList = taskList.map((curTask: any) => {
        if (!curTask.args) return curTask;

        let newArgs = {...curTask.args};

        if (properties) {
            const matchedProps = properties?.filter(
                (obj: any) => obj?.id === curTask?.args?.propertyId
            );

            newArgs.property = matchedProps.length > 0 ? matchedProps[0] : null;
        }
        return {
            ...curTask,
            args: newArgs,
        };
    });
    setVendorTaskList(updatedTaskList);
}


export const shouldDisableUpdateTax = (taskList: any) => {
    if (taskList) {
        for (let i = 0; i < taskList?.length; i++) {
            if (taskList[i].type === 'SCRAPER' && (taskList[i].status === 'PENDING' || taskList[i].status === 'NEW')) {
                return true;
            }
        }
    }

    return false;
}

export const propertyInfoFields = (
    curAccount: any,
    setIsSavedResult: any,
    certificate: any,
    setCertificate: any, // <-- new prop to update certificate immutably
    name: any,
    editable: any,
    setUpdatedAccountIndexList: any,
    showAccount: any,
    similarAccountList: any,
    setSimilarAccountList: any
) => {
    return (
        <>
            {name !== 'propertyId' ? (
                <InputField
                    placeholder="..."
                    hideLabel
                    type="text"
                    value={curAccount !== undefined && curAccount[name] !== null && curAccount[name] !== '' ? curAccount[name] : ''}
                    onChange={(e: any) => {
                        const updatedValue = e.target.value;

                        // Update curAccount immutably in certificate.accounts
                        const updatedCertificate = jsonCloneWithDates(certificate);
                        updatedCertificate.accounts[showAccount] = {
                            ...updatedCertificate.accounts[showAccount],
                            [name]: updatedValue,
                            taxDataSource: 'EDITOR',
                            asOfDate: new Date(),
                        };

                        setCertificate(updatedCertificate);
                        setIsSavedResult(false);
                        setUpdatedAccountIndexList((prev: number[]) =>
                            prev.includes(showAccount) ? prev : [...prev, showAccount]
                        );
                    }}
                    disabled={!editable}
                />
            ) : (
                <InputField
                    placeholder="..."
                    type="text"
                    hideLabel
                    value={curAccount !== undefined ? curAccount[name] : ''}
                    onChange={(e) => {
                        const updatedValue = e.target.value;

                        if (setSimilarAccountList !== null) {
                            const filtered = similarAccountList?.filter((el: any) => el.propertyId !== curAccount[name]);
                            setSimilarAccountList(filtered);
                        }

                        // Update curAccount immutably in certificate.accounts and propertyIds in certificate
                        const updatedCertificate = jsonCloneWithDates(certificate);

                        updatedCertificate.accounts[showAccount] = {
                            ...updatedCertificate.accounts[showAccount],
                            [name]: updatedValue,
                            taxDataSource: 'EDITOR',
                            asOfDate: new Date(),
                        };

                        updatedCertificate.propertyIds[showAccount] = {
                            id: updatedValue,
                            visibility: true,
                        };

                        setCertificate(updatedCertificate);
                        setIsSavedResult(false);
                        setUpdatedAccountIndexList((prev: number[]) =>
                            prev.includes(showAccount) ? prev : [...prev, showAccount]
                        );

                        // setAccounts([...accounts]); // optional
                    }}
                    disabled={!editable}
                />
            )}
        </>
    );
};

const validateAssessmentYear = (e: any, curAssessment: any, index: any, name: any, curAccount: any) => {
    if (e.target.value.length > 4) {
        curAssessment[name] = e.target.value.slice(0, 4);
        e.target.value = curAssessment[name];
    } else if (e.target.value.length === 4) {
        curAccount[name] = e.target.value;
        if (index === 0) {
            if (curAccount?.assessments[1][name].toString() === e.target.value.toString()) {
                curAssessment[name] = e.target.value.slice(0, 0);
                e.target.value = curAssessment[name];
            } else {
                curAssessment[name] = e.target.value;
                e.target.value = curAssessment[name];
            }
        } else if (index === 1) {
            if (curAccount?.assessments[0][name].toString() === e.target.value.toString()) {
                curAssessment[name] = e.target.value.slice(0, 0);
                e.target.value = curAssessment[name];
            } else {
                curAssessment[name] = e.target.value;
                e.target.value = curAssessment[name];
            }
        } else {
            curAssessment[name] = e.target.value;
            e.target.value = curAssessment[name];
        }
    } else {
        curAssessment[name] = e.target.value;
        e.target.value = curAssessment[name];
    }
}


export async function calculateAssessedValue(
    accountIndex: number,
    assessment: any,
    setRender: any,
    setCertificate: (cert: any) => void
): Promise<void> {
    try {
        const newValue = await dispatchUtilityRequest("calculateEditorValue", {
            calculation: "assessedValue",
            requestBody: assessment,
        });

        setCertificate((prev: any) => {
            const updatedAccounts = prev.accounts.map((acc: any, idx: number) => {
                if (idx !== accountIndex) return acc;

                // Default order: assessments (plural) is an array
                if (Array.isArray(acc.assessments)) {
                    const updatedAssessments = acc.assessments.map((a: any) =>
                        a.taxYear === assessment.taxYear ? {...a, assessedValue: newValue} : a
                    );
                    return {...acc, assessments: updatedAssessments};
                }

                // National order: assessment (singular) is an object
                if (acc.assessment?.taxYear === assessment.taxYear) {
                    return {
                        ...acc,
                        assessment: {
                            ...acc.assessment,
                            assessedValue: newValue,
                        },
                    };
                }

                return acc; // fallback
            });

            return {...prev, accounts: updatedAccounts};
        });

        setRender(Date.now());
    } catch (error) {
        console.error("Error calculating assessed value:", error);
    }
}

export async function calculateAgriculturalValue(
    accountIndex: number,
    assessment: any,
    setRender: any,
    setCertificate: (cert: any) => void
): Promise<void> {
    try {
        const newValue = await dispatchUtilityRequest("calculateEditorValue", {
            calculation: "agriculturalValue",
            requestBody: assessment,
        });

        setCertificate((prev: any) => {
            const updatedAccounts = prev.accounts.map((acc: any, idx: number) => {
                if (idx !== accountIndex) return acc;

                if (Array.isArray(acc.assessments)) {
                    const updatedAssessments = acc.assessments.map((a: any) =>
                        a.taxYear === assessment.taxYear ? {...a, agriculturalValue: newValue} : a
                    );
                    return {...acc, assessments: updatedAssessments};
                }

                if (acc.assessment?.taxYear === assessment.taxYear) {
                    return {
                        ...acc,
                        assessment: {
                            ...acc.assessment,
                            agriculturalValue: newValue,
                        },
                    };
                }

                return acc;
            });

            return {...prev, accounts: updatedAccounts};
        });

        setRender(Date.now());
    } catch (error) {
        console.error("Error calculating agricultural value:", error);
    }
}


export async function calculateLandTotalValue(
    accountIndex: number,
    assessment: any,
    setRender: any,
    setCertificate: (cert: any) => void
): Promise<void> {
    try {
        const newValue = await dispatchUtilityRequest("calculateEditorValue", {
            calculation: "landTotal",
            requestBody: assessment,
        });

        setCertificate((prev: any) => {
            const updatedAccounts = prev.accounts.map((acc: any, idx: number) => {
                if (idx !== accountIndex) return acc;

                if (Array.isArray(acc.assessments)) {
                    const updatedAssessments = acc.assessments.map((a: any) =>
                        a.taxYear === assessment.taxYear ? {...a, landTotalValue: newValue} : a
                    );
                    return {...acc, assessments: updatedAssessments};
                }

                if (acc.assessment?.taxYear === assessment.taxYear) {
                    return {
                        ...acc,
                        assessment: {
                            ...acc.assessment,
                            landTotalValue: newValue,
                        },
                    };
                }

                return acc;
            });

            return {...prev, accounts: updatedAccounts};
        });

        setRender(Date.now());
    } catch (error) {
        console.error("Error calculating land total value:", error);
    }
}

export async function calculateAppraisedValue(
    accountIndex: number,
    assessment: any,
    setRender: any,
    setCertificate: (cert: any) => void
): Promise<void> {
    try {
        const newValue = await dispatchUtilityRequest("calculateEditorValue", {
            calculation: "appraisedValue",
            requestBody: assessment,
        });

        setCertificate((prev: any) => {
            const updatedAccounts = prev.accounts.map((acc: any, idx: number) => {
                if (idx !== accountIndex) return acc;

                if (Array.isArray(acc.assessments)) {
                    const updatedAssessments = acc.assessments.map((a: any) =>
                        a.taxYear === assessment.taxYear ? {...a, appraisedValue: newValue} : a
                    );
                    return {...acc, assessments: updatedAssessments};
                }

                if (acc.assessment?.taxYear === assessment.taxYear) {
                    return {
                        ...acc,
                        assessment: {
                            ...acc.assessment,
                            appraisedValue: newValue,
                        },
                    };
                }

                return acc;
            });

            return {...prev, accounts: updatedAccounts};
        });

        setRender(Date.now());
    } catch (error) {
        console.error("Error calculating appraised value:", error);
    }
}

export const assessmentsAgRowFormatting = (
    setAssessmentIndex: any,
    setOverrideValue: any,
    curAccount: any,
    index: number,
    editable: boolean,
    certificate: any,
    setCertificate: any,
    name: string,
    setIsSavedResult: any,
    showAccount: number,
    setUpdatedAccountIndexList: any,
) => {
    const fieldValue = curAccount?.assessments?.[index]?.[name];

    return (
        <NumberFormat
            placeholder={
                fieldValue === null || fieldValue === '' || fieldValue === undefined ? "N/A" : "..."
            }
            className={
                fieldValue === null || fieldValue === '' || fieldValue === undefined
                    ? `form-control place_holder_data  ${!editable && "border border-text-0 bg-surface-5 text-surface-3"}`
                    : `form-control  ${!editable && "border border-text-0 bg-surface-5 text-surface-3"}`
            }
            fixedDecimalScale
            decimalScale={
                fieldValue !== undefined && fieldValue !== null && fieldValue !== ''
                    ? 2
                    : undefined
            }
            decimalSeparator="."
            isNumericString={fieldValue !== null && fieldValue !== ''}
            value={
                fieldValue === null || fieldValue === '' || fieldValue === undefined
                    ? "N/A"
                    : fieldValue
            }
            prefix={fieldValue !== null && fieldValue !== '' && fieldValue !== undefined ? "$" : ''}
            id={`${name}-${index}`}
            onChange={(e: any) => {
                if (!editable || !isAgExemptionExist(curAccount, index)) return;

                setAssessmentIndex(index);

                const rawValue = e.target.value;
                const cleanedValue = rawValue !== '' && rawValue !== null
                    ? parseFloat(rawValue.replace('$', '').replaceAll(',', ''))
                    : null;

                const updatedAccounts = [...certificate.accounts];
                const updatedAccount = {...updatedAccounts[showAccount]};
                const updatedAssessments = [...updatedAccount.assessments];
                const updatedAssessment = {...updatedAssessments[index]};

                updatedAssessment[name] = cleanedValue;
                updatedAssessments[index] = updatedAssessment;
                updatedAccount.assessments = updatedAssessments;
                updatedAccount.taxDataSource = "EDITOR";
                updatedAccount.asOfDate = new Date();
                updatedAccounts[showAccount] = updatedAccount;

                // Update certificate
                setCertificate({
                    ...certificate,
                    accounts: updatedAccounts
                });

                setOverrideValue(true);

                setUpdatedAccountIndexList((prev: any) =>
                    prev.includes(showAccount) ? prev : [...prev, showAccount]
                );

                setIsSavedResult(false);
            }}
            readOnly={!editable || !isAgExemptionExist(curAccount, index)}
        />
    );
};


export const isAgExemptionExist = (curAccount: any, index: number): boolean => {
    const exemptionList = curAccount?.assessments?.[index]?.exemptions;
    return Array.isArray(exemptionList) && exemptionList.includes("AG");
};


export const updateTotalBalance = (currentAccount: any, feeList: any) => {
    currentAccount['currentMonthTotal'] = feeList.reduce(function (prev: any, current: any) {
        if (current.currentMonth !== '' && current.currentMonth !== null) {
            return prev + parseFloat(current.currentMonth)
        } else {
            return prev;
        }
    }, 0);
    currentAccount['nextMonthTotal'] = feeList.reduce(function (prev: any, current: any) {
        if (current.nextMonth !== '' && current.nextMonth !== null) {
            return prev + parseFloat(current.nextMonth)
        } else {
            return prev;
        }
    }, 0);

    currentAccount['paidTotal'] = feeList.reduce(function (prev: any, current: any) {
        if (current.paid !== '' && current.paid !== null) {
            return prev + parseFloat(current.paid)
        } else {
            return prev;
        }
    }, 0);

    currentAccount['amountTotal'] = feeList.reduce(function (prev: any, current: any) {
        if (current.amount !== '' && current.amount !== null) {
            return prev + parseFloat(current.amount)
        } else {
            return prev;
        }
    }, 0);
}

export const assessmentsAgOverrideFormatting = (
    certificate: any,
    setCertificate: any,
    showAccount: number,
    index: number,
    name: string,
    readOnly: boolean,
    setName: any,
    setProductTypeInvisible: any,
    editable: boolean,
    overrideFieldName: string,
    setAssessmentIndex: any,
    setIsSavedResult: any,
    setRender = null
) => {
    const curAccount = certificate?.accounts?.[showAccount];
    const assessment = curAccount?.assessments?.[index];

    // Defensive fallback value
    const overrideValue = assessment?.[overrideFieldName];
    const baseValue = assessment?.[name];
    const valueToShow =
        certificate === undefined || curAccount === undefined || assessment === undefined
            ? 0
            : overrideValue === null || overrideValue === undefined
                ? baseValue ?? 0
                : overrideValue;

    const updateAssessmentValue = (newValue: any, override: boolean) => {
        const updatedAccounts = [...(certificate?.accounts || [])];
        const updatedAccount = {...updatedAccounts[showAccount]};
        const updatedAssessments = [...(updatedAccount?.assessments || [])];
        const updatedAssessment = {...updatedAssessments[index]};

        updatedAssessment[overrideFieldName] = override ? newValue : null;

        updatedAssessments[index] = updatedAssessment;
        updatedAccount.assessments = updatedAssessments;
        updatedAccount.taxDataSource = 'EDITOR';
        updatedAccount.asOfDate = new Date();
        updatedAccounts[showAccount] = updatedAccount;

        setCertificate({
            ...certificate,
            accounts: updatedAccounts,
        });

        if (override) {
            setName(name);
            setProductTypeInvisible(newValue + new Date().getTime());

            if (name === "landTotalValue") {
                calculateAppraisedValue(showAccount, updatedAssessment, setRender, setCertificate);
            } else {
                calculateAssessedValue(showAccount, updatedAssessment, setRender, setCertificate);
            }
        } else {
            if (editable && isAgExemptionExist(updatedAccount, index) && name === "agriculturalValue") {
                calculateAgriculturalValue(showAccount, updatedAssessment, setRender, setCertificate);
                calculateAssessedValue(showAccount, updatedAssessment, setRender, setCertificate);
            } else if (name === "landTotalValue") {
                calculateLandTotalValue(showAccount, updatedAssessment, setRender, setCertificate);
                calculateAppraisedValue(showAccount, updatedAssessment, setRender, setCertificate);
            }
        }

        setIsSavedResult(false);
    };

    return (
        <div className="assessment_data relative">
            <NumberFormat
                placeholder={(overrideValue === null || overrideValue === undefined) ? "N/A" : "..."}
                className={`form-control ${!editable ? "border border-text-0 bg-surface-5 text-surface-3" : ""} ${(overrideValue === null || overrideValue === undefined) ? "place_holder_data" : ""
                }`}
                id={`${name}-${index}`}
                decimalScale={2}
                decimalSeparator="."
                fixedDecimalScale
                value={valueToShow}
                isNumericString
                onChange={(e: any) => {
                    if (!editable || overrideValue === null) return;

                    const cleanedValue = e.target.value !== ''
                        ? parseFloat(e.target.value.replace('$', '').replaceAll(',', ''))
                        : '';

                    updateAssessmentValue(cleanedValue, true);
                }}
                prefix={(overrideValue !== null || baseValue !== null) ? '$' : ''}
                thousandSeparator
                readOnly={!editable || overrideValue === null}
            />

            <div className="assessment_icons absolute right-3 top-3">
                {overrideValue == null ? (
                    <i
                        className={
                            (editable && isAgExemptionExist(curAccount, index)) || name !== "agriculturalValue"
                                ? "fa fa-pen ml-auto readonly_icon"
                                : "fa fa-pen ml-auto disableSelection"
                        }
                        onClick={() => {
                            if ((editable && isAgExemptionExist(curAccount, index)) || name !== "agriculturalValue") {
                                updateAssessmentValue(baseValue ?? 0, true);
                            }
                        }}
                    />
                ) : (
                    <i
                        className={
                            (editable && isAgExemptionExist(curAccount, index)) || name !== "agriculturalValue"
                                ? "fa fa-rotate-right readonly_icon"
                                : "fa fa-rotate-right disableSelection"
                        }
                        onClick={() => updateAssessmentValue(null, false)}
                    />
                )}
            </div>
        </div>
    );
};

export const exemptionsList = (
    itemMap: any,
    label: any,
    value: any,
    placeholderText: any,
    isMulti: boolean,
    isRequired: boolean,
    curAccount: any,
    i: number,
    setProductTypeInvisible: Function,
    setIsSavedResult: Function,
    showAccount: any,
    setUpdatedAccountIndexList: React.Dispatch<React.SetStateAction<number[]>>,
    certificate: any,
    setCertificate: any
) => {
    if (!Array.isArray(curAccount?.assessments) || !curAccount.assessments[i]) {
        return null;
    }

    const assessment = curAccount.assessments[i];

    const options = Array.isArray(itemMap)
        ? itemMap.map((item: any) => ({
            value: typeof value === 'function' ? value(item) : item[value],
            label: typeof label === 'function' ? label(item) : item[label],
        }))
        : [];

    const handleChange = (selected: any) => {
        const selectedValues = Array.isArray(selected)
            ? selected.map((opt) => opt?.value)
            : [selected?.value];

        // Create a new assessment with updated exemptions
        const updatedAssessment = {
            ...assessment,
            exemptions: selectedValues,
        };

        const updatedAssessments = [...curAccount.assessments];
        updatedAssessments[i] = updatedAssessment;

        const updatedAccount = {
            ...curAccount,
            assessments: updatedAssessments,
        };

        const updatedAccounts = [...certificate.accounts];
        updatedAccounts[showAccount] = updatedAccount;

        setCertificate({
            ...certificate,
            accounts: updatedAccounts,
        });

        setIsSavedResult(false);

        setUpdatedAccountIndexList((prevList) => {
            if (!prevList.includes(showAccount)) {
                return [...prevList, showAccount];
            }
            return prevList;
        });

        setProductTypeInvisible(Date.now());
    };

    const selectedOptions = Array.isArray(assessment?.exemptions)
        ? assessment.exemptions.map((val: any) => {
            const match = options.find((opt) => opt.value === val);
            return match || {label: val, value: val};
        })
        : [];

    return (
        <SelectField
            placeholder={placeholderText}
            value={selectedOptions}
            options={options}
            onChange={handleChange}
            hideLabel
            label=""
            isMulti={isMulti}
            required={isRequired}
            isDisabled={!options || !options.length}
            className={'col-span-12'}
        />
    );
};

export const assessmentsOverrideFormatting = (
    curAccount: any,
    index: any,
    name: any,
    readOnly: any,
    setName: any,
    setProductTypeInvisible: any,
    editable: any,
    overrideFieldName: any,
    setAssessmentIndex: any,
    setIsSavedResult: any,
    setRender: any,
    showAccount: any,
    setUpdatedAccountIndexList: any,  // Add setter for immutability
    setCertificate: any,
    certificate: any
) => {
    return (
        <>
            <div className="assessment_data relative">
                {
                    Array.isArray(curAccount?.assessments) && curAccount.assessments[index] ? (
                        <NumberFormat
                            placeholder={
                                curAccount.assessments[index]?.[overrideFieldName] == null
                                    ? "N/A"
                                    : "..."
                            }
                            className={
                                curAccount.assessments[index]?.[overrideFieldName] == null
                                    ? `form-control place_holder_data bg-text-8 ${!editable && "border border-text-1 bg-surface-5 text-surface-3"}`
                                    : `form-control  ${!editable && "border border-text-1 bg-surface-5 text-surface-3"}`
                            }
                            id={`${name}-${index}`}
                            decimalScale={
                                (curAccount?.assessments[index][overrideFieldName] != null || curAccount?.assessments[index][name] != null)
                                    ? ((name !== 'woExemptionAmount' && name !== 'withExemptionEstimate') ? 0 : 2)
                                    : undefined
                            }
                            decimalSeparator="."
                            fixedDecimalScale
                            isNumericString={
                                curAccount.assessments[index]?.[overrideFieldName] != null ||
                                curAccount.assessments[index]?.[name] != null
                            }
                            value={
                                curAccount.assessments[index]?.[overrideFieldName] == null
                                    ? (curAccount.assessments[index]?.[name] == null ? "N/A" : curAccount.assessments[index]?.[name])
                                    : curAccount.assessments[index]?.[overrideFieldName]
                            }
                            onChange={async (e: any) => {
                                const newValue =
                                    e.target.value !== ''
                                        ? parseFloat(e.target.value.replace('$', '').replaceAll(',', ''))
                                        : e.target.value;

                                // Clone certificate
                                let updatedCertificate = jsonCloneWithDates(certificate);
                                const updatedAccount = updatedCertificate.accounts[showAccount];

                                // Update override field
                                updatedAccount.assessments[index] = {
                                    ...updatedAccount.assessments[index],
                                    [overrideFieldName]: newValue,
                                };
                                updatedAccount.taxDataSource = 'EDITOR';
                                updatedAccount.asOfDate = new Date();

                                const assessment = updatedAccount.assessments[index];
                                setCertificate(updatedCertificate);

                                // Conditional calculations
                                if (name === "appraisedValue" && assessment?.taxYear >= getCurrentTaxYear()) {
                                    calculateCurrentWoExemptAmountValue(
                                        updatedCertificate,
                                        showAccount,
                                        assessment,
                                        setRender,
                                        setCertificate
                                    );
                                }

                                if (name === "assessedValue" && assessment?.taxYear >= getCurrentTaxYear()) {
                                    calculateCurrentWithExemptionEstimateValue(
                                        updatedCertificate,
                                        showAccount,
                                        assessment,
                                        setRender,
                                        setCertificate
                                    );
                                }

                                setName(name);
                                setProductTypeInvisible(e.target.value + new Date().getTime());
                                setIsSavedResult(false);

                                setUpdatedAccountIndexList((prev: number[]) =>
                                    prev.includes(showAccount) ? prev : [...prev, showAccount]
                                );
                            }}
                            prefix={
                                (curAccount.assessments[index]?.[overrideFieldName] != null || curAccount.assessments[index]?.[name] != null) ? '$' : ''
                            }
                            thousandSeparator={true}
                            readOnly={
                                !editable ||
                                curAccount.assessments[index]?.[overrideFieldName] === null
                            }
                        />
                    ) : null
                }

                <div className="assessment_icons absolute top-2 right-0">
                    {
                        Array.isArray(curAccount?.assessments) && curAccount.assessments[index] ? (
                            curAccount.assessments[index]?.[overrideFieldName] == null ? (
                                <i
                                    className="fa fa-pen ml-auto mr-2 readonly_icon"
                                    onClick={() => {
                                        if (editable === true) {
                                            const updatedCertificate = jsonCloneWithDates(certificate);
                                            const updatedAccount = updatedCertificate.accounts[showAccount];

                                            updatedAccount.assessments[index] = {
                                                ...updatedAccount.assessments[index],
                                                [overrideFieldName]: updatedAccount.assessments[index]?.[name] ?? 0,
                                            };

                                            setCertificate(updatedCertificate);
                                        }
                                    }}
                                />
                            ) : (
                                <i
                                    className="fa fa-rotate-right mr-2 readonly_icon"
                                    onClick={() => {
                                        if (!editable) return;

                                        curAccount.assessments[index][overrideFieldName] = null;
                                        let updatedCertificate = jsonCloneWithDates(certificate);
                                        const updatedAccount = updatedCertificate.accounts[showAccount];
                                        const assessment = updatedAccount.assessments[index];

                                        // Clear override field
                                        updatedAccount.assessments[index] = {
                                            ...assessment,
                                            [overrideFieldName]: null,
                                        };
                                        if (name === 'appraisedValue') {
                                            calculateAppraisedValue(showAccount, assessment, setRender, setCertificate);

                                            if (assessment?.taxYear >= getCurrentTaxYear()) {
                                                calculateCurrentWoExemptAmountValue(
                                                    updatedCertificate,
                                                    showAccount,
                                                    assessment,
                                                    setRender,
                                                    setCertificate
                                                );
                                            }
                                        } else {
                                            calculateAssessedValue(showAccount, assessment, setRender, setCertificate);
                                            if (assessment?.taxYear >= getCurrentTaxYear()) {
                                                calculateCurrentWithExemptionEstimateValue(
                                                    updatedCertificate,
                                                    showAccount,
                                                    assessment,
                                                    setRender,
                                                    setCertificate
                                                );
                                            }
                                        }

                                    }}
                                />
                            )
                        ) : null
                    }
                </div>
            </div>
        </>
    );
};


export async function calculateCurrentWithExemptionEstimateValue(
    certificate: any,
    accountIndex: number,
    assessment: any,
    setRender: any,
    setCertificate: (cert: any) => void
): Promise<void> {
    try {
        const account = certificate.accounts[accountIndex];
        if (!account) return;

        const updatedJurisdictions = populateUpdatedJurisdiction(account);

        const accountPayload = {
            ...account,
            jurisdictionList: updatedJurisdictions,
        };

        const data = await dispatchUtilityRequest("calculateWithExemptionEstimate", {
            taxYear: assessment.taxYear,
            requestBody: accountPayload,
        });

        setCertificate((prev: any) => ({
            ...prev,
            accounts: prev?.accounts?.map((acc: any, accIdx: number) => {
                if (accIdx !== accountIndex) return acc;

                const updatedAssessments = acc?.assessments?.map((a: any) =>
                    a.taxYear === assessment.taxYear
                        ? {...a, withExemptionEstimate: data}
                        : a
                );

                return {
                    ...acc,
                    jurisdictions: updatedJurisdictions,
                    assessments: updatedAssessments,
                };
            }),
        }));

        setRender(Date.now());
    } catch (error) {
        console.error("Error in calculateCurrentWithExemptionEstimateValue:", error);
    }
}


export async function calculateCurrentWoExemptAmountValue(
    certificate: any,
    accountIndex: number,
    assessment: any,
    setRender: any,
    setCertificate: (cert: any) => void
): Promise<void> {
    try {
        const data = await dispatchUtilityRequest("calculateCurrentWoExemptAmount", {
            taxYear: assessment.taxYear,
            requestBody: certificate.accounts[accountIndex],
        });

        setCertificate((prev: any) => {
            return {
                ...prev,
                accounts: prev?.accounts?.map((acc: any, accIdx: number) => {
                    if (accIdx !== accountIndex) return acc;

                    const updatedAssessments = acc?.assessments?.map((a: any) => {
                        if (a.id !== assessment.id) return a;
                        return {
                            ...a,
                            woExemptionAmount: data,
                        };
                    });

                    return {
                        ...acc,
                        jurisdictions: populateUpdatedJurisdiction(acc),
                        assessments: updatedAssessments,
                    };
                }),
            };
        });

        setRender(Date.now());
    } catch (error) {
        console.error("Error in calculateCurrentWoExemptAmountValue:", error);
    }
}


export function populateUpdatedJurisdiction(account: any) {
    const jurisdictions: any[] = [];

    account?.jurisdictionList?.forEach((el: any) => {
        const bonds = account?.bondList?.filter((obj: any) => obj.jurisdictionId === el.jurisdictionId);
        const summary = account?.summaryList?.filter(
            (obj: any) => obj.collector === el.collectorName && obj.taxYear === el.bill_year
        );

        const jurisdictionId = el.jurisdictionId;
        const jurisdictionName = el.jurisdictionName;
        const collectorId = el.collectorId;
        const collectorAccountNumber = summary?.[0]?.collectorAccountNumber ?? null;

        const shouldInclude =
            (jurisdictionId && collectorId && el.placeHolder === false) || el.placeHolder === true;

        if (!shouldInclude) return;

        const billYear = el.bill_year;
        const baseDue = el.baseDue;
        const baseTax = el.baseTax;
        const taxRate =
            el.tax_rate != null && el.tax_rate !== ''
                ? parseFloat(el.tax_rate.toString().replaceAll(',', ''))
                : el.tax_rate;
        const asOfDate = el.asOfDate;
        const taxDataSource = el.taxDataSource;
        const currentMonthDue = el.currentMonthDue;
        const nextMonthDue = el.nextMonthTaxDue;
        const inActive = el.inActive;
        const nonCollecting = el.nonCollecting;
        const placeHolder = el.placeHolder;

        const index = jurisdictions.findIndex(
            (obj: any) => obj.collectorId === el.collectorId && obj.jurisdictionId === el.jurisdictionId
        );

        const levy = {billYear, taxRate, baseTax, baseDue, currentMonthDue, nextMonthDue};

        if (index === -1) {
            jurisdictions.push({
                jurisdictionId,
                jurisdictionName,
                collectorAccountNumber,
                asOfDate,
                taxDataSource,
                collectorId,
                levies: [levy],
                bonds,
                placeHolder,
                inActive,
                nonCollecting
            });
        } else {
            const existing = jurisdictions[index];
            const existingLevyIndex = existing.levies.findIndex((obj: any) => obj.billYear === billYear);

            const newLevies =
                existingLevyIndex !== -1
                    ? existing.levies.map((l: any, i: number) => (i === existingLevyIndex ? levy : l))
                    : [...existing.levies, levy];

            jurisdictions[index] = {
                ...existing,
                levies: newLevies
            };
        }
    });

    return jurisdictions;
}


export const summaryHeadDefault = () => {
    return (
        <>
            <thead>
            <tr>
                <th scope="col" className=" bg-highlight-3 text-text-0">Collector
                </th>
                <th scope="col" className=" bg-highlight-3 text-text-0">Account Number
                </th>
                <th scope="col" className=" bg-highlight-3 text-text-0">Tax Year</th>
                <th scope="col" className=" bg-highlight-3 text-text-0">Base Tax
                </th>
                <th scope="col" className=" bg-highlight-3 text-text-0">Base Due
                </th>
                <th scope="col" className=" bg-highlight-3 text-text-0">If Paid By
                    {' ' + new Intl.DateTimeFormat('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                    }).format(currentMonthLastDate)}</th>
                <th scope="col" className=" bg-highlight-3 text-text-0">If Paid By
                    {' ' + new Intl.DateTimeFormat('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                    }).format(nextMonthLastDate)}</th>

                <th scope="col" className=" bg-highlight-3 text-text-0">As of Date
                </th>
            </tr>


            </thead>
        </>
    )
}
export const summaryRowFormatting = (curAccount: any, name: any) => {
    {


        return (
            <>

                <NumberFormat
                    placeholder='...'
                    className="border-0 text-center outline_remove1 readonly_bg"
                    decimalScale={
                        curAccount !== undefined && curAccount[name] !== null && curAccount[name] !== ''
                            ? 2
                            : undefined
                    } decimalSeparator="."
                    fixedDecimalScale
                    isNumericString={curAccount !== undefined && (curAccount[name] !== null || curAccount[name] !== '')}
                    value={curAccount === undefined || (curAccount[name] === null || curAccount[name] === '') ? "N/A" : curAccount[name]}
                    prefix={curAccount !== undefined && (curAccount[name] !== null && curAccount[name] !== '') ? "$" : ''}
                    thousandSeparator={true}
                    disabled={true}
                />
            </>
        )

    }
}

export function sortCadInfo(cadInfo: any) {
    return []
        .concat(...cadInfo)
        .filter((ci: any) => ci && ci.taxingUnit)
        .sort((a: any, b: any) => a.taxingUnit.localeCompare(b.taxingUnit));
}

export const jurisdictionHead = (setShowJurisdictionPickerModal: any, editable: any) => {
    return (
        <>
            <thead>
            <tr>
                <th scope="col" className="text-center  bg-highlight-3 text-text-0"><i
                    className="fa fa-plus-circle onHover"
                    onClick={() => {
                        if (editable)
                            setShowJurisdictionPickerModal(true);
                    }}
                    title={"Add Jurisdiction"}/></th>
                <th scope="col" className="text-center  bg-highlight-3 text-text-0 "><a
                    className="grid-color toggle-data">Collector*</a>
                </th>
                <th scope="col" className="text-center  bg-highlight-3 text-text-0 min-w-52"><a
                    className="grid-color toggle-data">Jurisdiction*</a></th>
                <th scope="col" className="text-center  bg-highlight-3 text-text-0"><a
                    className="grid-color toggle-data">Bill Year</a>
                </th>
                <th scope="col" className="text-center  bg-highlight-3 text-text-0"><a
                    className="grid-color toggle-data">Tax Rate</a>
                </th>
                <th scope="col" className="text-center  bg-highlight-3 text-text-0"><a
                    className="grid-color toggle-data">Base Tax</a>
                </th>
                <th scope="col" className="text-center  bg-highlight-3 text-text-0"><a
                    className="grid-color toggle-data">Base Due</a>
                </th>
                <th scope="col" className="text-center  bg-highlight-3 text-text-0"><a
                    className="grid-color toggle-data">Due {formatValueAsDate(moment().format("MM/DD/YYYY"))}</a>

                </th>
                <th scope="col" className="text-center  bg-highlight-3 text-text-0"><a
                    className="grid-color toggle-data">Due {formatValueAsDate(moment().add(1, 'months').format("MM/DD/YYYY"))}</a>
                </th>

                <th scope="col" className="text-center  bg-highlight-3 text-text-0"><a
                    className="grid-color toggle-data">Source</a>
                </th>

            </tr>
            </thead>
        </>
    )
}

export function calculateAsOfDate(row: any) {
    row['asOfDate'] = new Date();
    row['taxDataSource'] = 'EDITOR';
}

export const jurisdictionYearFields = (row: any, setJurisdictionId: any, p: any, curAccount: any, setAccounts: any, accounts: any, setUpdateTotalTaxValue: any, setTaxYear: any, setIsAddNewSummaryRow: any, name: any, disabled: any, setIsSavedResult: any, editable: any, setAutoCalculate: any) => {
    let style = 'border-0 p-3 pr-0 text-center w-100';
    style = style + (row['placeHolder'] && row['name'] ? ' place_holder' : '');
    style = style + (!row['name'] ? ' text-surface-3 ' : '');

    return (
        <>
            <input
                className={style}
                placeholder={!row[name] ? "N/A" : 'Year...'}
                type='number'
                name='bill_year'
                min={minYear}
                max={maxYear}
                value={row[name]}
                onChange={(e) => {
                    row["placeHolder"] = false;
                    setJurisdictionId(p);
                    setIsSavedResult(false);
                    validateYear(e, p, name, curAccount.jurisdictionList, setUpdateTotalTaxValue, setTaxYear, setJurisdictionId, setIsAddNewSummaryRow);
                    calculateAsOfDate(row);
                    setAutoCalculate(true);
                    setAccounts([...accounts]);
                }}
                readOnly={disabled || !editable}
            />
        </>
    )
}

const validateYear = (e: any, index: any, name: any, listType: any, setUpdateTotalTaxValue: any, setTaxYear: any, setJurisdictionId: any, setIsAddNewSummaryRow: any) => {
    if (e.target.value.length > 4) {
        listType[index][name] = e.target.value.slice(0, 4);
        e.target.value = listType[index][name];
    }

    if (e.target.value.length === 4) {
        if (!(e.target.value > minYear && e.target.value <= maxYear)) {
            listType[index][name] = e.target.value.slice(0, 0);
            e.target.value = listType[index][name];
        } else {
            listType[index][name] = e.target.value;
            if (name === 'bill_year') {
                let result = listType[index]['tax_rate_List'].filter((value1: any) => value1.year.toString() === e.target.value);
                if (result.length > 0 && listType[index]['nonCollecting'] === false) {
                    listType[index]['tax_rate'] = result[0]['rate'];
                } else {
                    listType[index]['tax_rate'] = '';
                }
                setUpdateTotalTaxValue(true);
            }
            e.target.value = listType[index][name];
        }
    } else {
        listType[index][name] = e.target.value;
        e.target.value = listType[index][name];
    }

    if (name === 'bill_year' && e.target.value.length === 4) {
        setTaxYear(e.target.value);
        setJurisdictionId(index)
        setIsAddNewSummaryRow(true);
    }
}

export const countDecimals = (str1: any) => {
    let str = str1?.toString();
    if (str?.indexOf(".") !== -1 && str?.indexOf("-") !== -1) {
        return str?.split("-")[1] || 0;
    } else if (str?.indexOf(".") !== -1) {
        return str?.split(".")[1].length || 0;
    }
    return str?.split("-")[1] || 0;
}

export const getShortenedTaxDataSource = (taxDataSource: any) => {
    let letter = taxDataSource.slice(0, 1)
    return letter === 'U' ? '-' : letter;
}
export const getUserFriendlyTaxDataSource = (taxDataSource: any) => {
    switch (taxDataSource) {
        case 'ROLL':
            return 'Rolls'
        case 'SUBDIVISION':
            return 'Subdivision';
        case 'EDITOR':
            return 'Editor';
        case 'SCRAPER':
            return 'Web';
        case 'DATATERRITORY':
            return 'Data Territory';
        default:
            return 'Unknown';
    }
}

export const BondHead = (setAddedNewBond: any, editable: any) => {
    return (
        <>
            <thead>
            <tr>
                <th className="text-center  bg-highlight-3 text-text-0" scope="col"><i
                    className="fa fa-plus-circle onHover"
                    onClick={() => {
                        if (editable)
                            setAddedNewBond(true)
                    }} title={"Add Bond"}/>
                </th>
                <th scope="col" className="text-center  bg-highlight-3 text-text-0"><a
                    className="grid-color toggle-data">Jurisdiction Name</a>
                </th>
                <th scope="col" className="text-center  bg-highlight-3 text-text-0"><a
                    className="grid-color toggle-data">Bonds Approved</a></th>
                <th scope="col" className="text-center  bg-highlight-3 text-text-0"><a
                    className="grid-color toggle-data">Bonds Issued</a>
                </th>
                <th scope="col" className="text-center  bg-highlight-3 text-text-0"><a
                    className="grid-color toggle-data">Standby Fees</a>
                </th>
            </tr>
            </thead>
        </>
    )
}


export const bondRowFormatting = (
    row: any,
    setCertificate: React.Dispatch<React.SetStateAction<any>>,
    setBondId: React.Dispatch<React.SetStateAction<number>>,
    p: number,
    certificate: any,
    name: string,
    setIsSavedResult: React.Dispatch<React.SetStateAction<boolean>>,
    editable: boolean,
    showAccount: number,
    setUpdatedAccountIndexList: any
) => {
    const value = row?.[name];
    const isValueEmpty = value === null || value === '' || value === undefined;
    const isDisabled =
        !editable ||
        !row?.jurisdictionName ||
        !row?.collectorName;

    const updateCertificate = (newValue: string) => {
        // Assuming `certificate` contains an `accounts` array and each account has a `bondList`
        const updatedCertificate = {...certificate};
        updatedCertificate.accounts[showAccount].bondList[p][name] = newValue;

        setCertificate(updatedCertificate);
    };

    return (
        <NumberFormat
            placeholder={isValueEmpty ? "N/A" : "..."}
            className={`border-0 form-control text-center ${isDisabled ? "bg-text-8 " : "bg-text-0"}`}
            decimalScale={!isValueEmpty ? 2 : undefined}
            decimalSeparator="."
            fixedDecimalScale
            isNumericString={!isValueEmpty}
            value={isValueEmpty ? "N/A" : value}
            prefix={!isValueEmpty ? "$" : ""}
            thousandSeparator
            onChange={(e: any) => {
                setBondId(p);

                // Immutably update the account index list, avoiding duplicates
                setUpdatedAccountIndexList((prev: any) => {
                    if (!prev.includes(showAccount)) {
                        return [...prev, showAccount];
                    }
                    return prev;
                });

                const newValue = e.target.value.replace('$', '').replaceAll(',', '');
                updateCertificate(newValue);
                setIsSavedResult(false);
            }}
            disabled={isDisabled}
            readOnly={!editable}
        />
    );
};

export const SuitsHead = (setAddedNewSuit: any, editable: any) => {
    return (
        <>
            <thead>
            <tr>
                <th className="text-center  bg-highlight-3 text-text-0" scope="col"><i
                    className="fa fa-plus-circle onHover"
                    onClick={() => {
                        if (editable)
                            setAddedNewSuit(true)
                    }} title={"Add Tax Suit"}/>
                </th>
                <th scope="col" className="text-center  bg-highlight-3 text-text-0"><a
                    className="grid-color toggle-data">Payee</a>
                </th>
                <th scope="col" className="text-center  bg-highlight-3 text-text-0"><a
                    className="grid-color toggle-data">Suit Number</a></th>
                <th scope="col" className="text-center  bg-highlight-3 text-text-0"><a
                    className="grid-color toggle-data">Parcel</a>
                </th>
                <th scope="col" className="text-center  bg-highlight-3 text-text-0"><a
                    className="grid-color toggle-data">Fee Type</a>
                </th>
                <th scope="col" className="text-center  bg-highlight-3 text-text-0"><a
                    className="grid-color toggle-data">Amount</a>
                </th>
                <th scope="col" className="text-center  bg-highlight-3 text-text-0"><a
                    className="grid-color toggle-data">Balance</a>
                </th>
            </tr>
            </thead>
        </>
    )
}


export const taxInputRow = (
    row: any,
    name: any,
    setIsSavedResult: any,
    setCertificate: any,
    editable: any,
    showAccount: any,
    setUpdatedAccountIndexList: any,
    rowIndex: number // Add this
) => {
    return (
        <input
            className={row['payee'] === ''
                ? "bg-text-8 border-0 form-control selector text-center place_holder_data"
                : "border-0 form-control selector text-center"}
            placeholder="..."
            value={row[name]}
            onChange={(e) => {
                const newValue = e.target.value;

                setCertificate((prevCertificate: any) => {
                    const updatedCertificate = {...prevCertificate};
                    updatedCertificate.accounts[showAccount].taxSuits[rowIndex][name] = newValue;
                    return updatedCertificate;
                });

                setUpdatedAccountIndexList((prev: number[]) =>
                    prev.includes(showAccount) ? prev : [...prev, showAccount]
                );

                setIsSavedResult(false);
            }}
            readOnly={!editable}
            disabled={row['payee'] === ''}
        />

    );
};


export const SuitRowFormatting = (
    row: any,
    setCertificate: any,
    setSuitId: any,
    rowIndex: number,
    certificate: any,
    name: any,
    setIsSavedResult: any,
    editable: any,
    showAccount: any,
    setUpdatedAccountIndexList: (updateFn: (prev: number[]) => number[]) => void
) => {
    return (
        <NumberFormat
            placeholder={(row[name] === null || row[name] === '' || row[name] === undefined) ? "N/A" : '...'}
            className={row['payee'] === '' ||
            row['suitNumber'] === '' ||
            row['parcel'] === '' ||
            row['feeType'] === '' ||
            !editable
                ? "bg-text-8 border-0 form-control selector text-center place_holder_data"
                : "border-0 form-control selector text-center"}
            decimalScale={row[name] !== null && row[name] !== '' ? 2 : undefined}
            decimalSeparator="."
            fixedDecimalScale
            isNumericString={row[name] !== null && row[name] !== ''}
            value={row[name] === null || row[name] === '' ? "N/A" : row[name]}
            prefix={row[name] !== null && row[name] !== '' ? "$" : ''}
            thousandSeparator={true}
            onChange={(e: any) => {
                setSuitId(rowIndex);

                const value = e.target.value.replace('$', '').replaceAll(',', '');
                setCertificate((prevCertificate: any) => {
                    const updatedCertificate = {...prevCertificate};
                    updatedCertificate.accounts[showAccount].taxSuits[rowIndex][name] = value;
                    return updatedCertificate;
                });

                setIsSavedResult(false);
                setUpdatedAccountIndexList((prev) =>
                    prev.includes(showAccount) ? prev : [...prev, showAccount]
                );
            }}
            readOnly={
                row['payee'] === '' ||
                row['suitNumber'] === '' ||
                row['parcel'] === '' ||
                row['feeType'] === '' ||
                !editable
            }
        />
    );
};

export function calculateTotalTaxValue(certificate: any, showAccount: any, setRender: any, setCertificate: any) {
    // Clone certificate to avoid mutating props/state directly
    const updatedCertificate = jsonCloneWithDates(certificate);

    // Step 1: Set taxRateTotal immediately
    const jurisdictions = getUniqueLatestYearJurisdictions(
        updatedCertificate.accounts[showAccount].jurisdictionList
    );

    const taxRateTotal = jurisdictions.reduce((prev: any, current: any) => {
        if (current.tax_rate !== '' && current.tax_rate !== null && prev !== null) {
            return prev + parseFloat(current.tax_rate);
        } else if (current.tax_rate === '' || current.tax_rate === null || prev === null) {
            return null;
        } else {
            return prev;
        }
    }, 0);

    setCertificate((prev: any) => ({
        ...prev,
        accounts: prev.accounts.map((acc: any, index: any) =>
            index === showAccount ? {...acc, taxRateTotal: taxRateTotal} : acc
        )
    }));

    recalculateAssessment(showAccount, setRender, updatedCertificate, setCertificate);

}


export function getUniqueLatestYearJurisdictions(jurisdictionList: any) {
    let list: any = [];
    for (let i = 0; i < jurisdictionList.length; i++) {
        if (jurisdictionList[i]['jurisdictionId'] !== '' && jurisdictionList[i]['jurisdictionId'] !== null && jurisdictionList[i]['inActive'] !== true && jurisdictionList[i]['nonCollecting'] !== true) {
            let index = list.findIndex((obj: any) => obj.jurisdictionName === jurisdictionList[i]['jurisdictionName']);
            if (index > -1) {
                if (parseInt(jurisdictionList[i]['bill_year']) > parseInt(list[index]['bill_year'])) {
                    list[index] = jurisdictionList[i];
                }
            } else {
                list.push(jurisdictionList[i]);
            }
        }
    }
    return list;
}

export async function recalculateAssessment(
    i: number,
    setRender: React.Dispatch<React.SetStateAction<number>>,
    certificate: any,
    setCertificate: React.Dispatch<React.SetStateAction<any>>
): Promise<void> {
    try {
        // clone to avoid mutation
        const updatedCertificate = jsonCloneWithDates(certificate);

        const account = updatedCertificate.accounts[i];
        if (!account || account.assessments?.length < 2) {
            return;
        }

        const [obj1, obj2] = [jsonCloneWithDates(account.assessments[0]), jsonCloneWithDates(account.assessments[1])];

        // API calls
        const [
            assessedValue0, assessedValue1, appraisedValue0, appraisedValue1,
            agriculturalValue0, agriculturalValue1, landTotalValue0, landTotalValue1
        ] = await Promise.all([
            dispatchUtilityRequest("calculateEditorValue", {calculation: "assessedValue", requestBody: obj1}),
            dispatchUtilityRequest("calculateEditorValue", {calculation: "assessedValue", requestBody: obj2}),
            dispatchUtilityRequest("calculateEditorValue", {calculation: "appraisedValue", requestBody: obj1}),
            dispatchUtilityRequest("calculateEditorValue", {calculation: "appraisedValue", requestBody: obj2}),
            dispatchUtilityRequest("calculateEditorValue", {calculation: "agriculturalValue", requestBody: obj1}),
            dispatchUtilityRequest("calculateEditorValue", {calculation: "agriculturalValue", requestBody: obj2}),
            dispatchUtilityRequest("calculateEditorValue", {calculation: "landTotal", requestBody: obj1}),
            dispatchUtilityRequest("calculateEditorValue", {calculation: "landTotal", requestBody: obj2}),
        ]);

        populateUpdatedJurisdiction(account); // mutate account if needed

        account.assessments[0] = {
            ...account.assessments[0],
            assessedValue: assessedValue0,
            appraisedValue: appraisedValue0,
            agriculturalValue: agriculturalValue0,
            landTotalValue: landTotalValue0,
        };
        account.assessments[1] = {
            ...account.assessments[1],
            assessedValue: assessedValue1,
            appraisedValue: appraisedValue1,
            agriculturalValue: agriculturalValue1,
            landTotalValue: landTotalValue1,
        };

        setRender(prev => prev + 1);

        setCertificate((prev: any) => {
            // Defensive: check account/assessments exist
            const prevAccount = prev.accounts?.[i];
            if (!prevAccount || !Array.isArray(prevAccount.assessments)) return prev;

            // Clone assessments and update only 0 and 1
            const updatedAssessments = prevAccount.assessments.map((a: any, idx: number) => {
                if (idx === 0) {
                    return {
                        ...a,
                        assessedValue: assessedValue0,
                        appraisedValue: appraisedValue0,
                        agriculturalValue: agriculturalValue0,
                        landTotalValue: landTotalValue0,
                    };
                }
                if (idx === 1) {
                    return {
                        ...a,
                        assessedValue: assessedValue1,
                        appraisedValue: appraisedValue1,
                        agriculturalValue: agriculturalValue1,
                        landTotalValue: landTotalValue1,
                    };
                }
                return a; // untouched assessments
            });

            const updatedAccounts = prev.accounts.map((acc: any, idx: number) => {
                if (idx !== i) return acc;

                return {
                    ...acc,
                    assessments: updatedAssessments,
                };
            });

            return {
                ...prev,
                accounts: updatedAccounts,
            };
        });

    } catch (error) {
        console.error("Error in recalculateAssessment:", error);
    }
}


export function updateSummaryTotalRowCalculation(currentAccount: any, summaryList: any) {
    if (!summaryList) return currentAccount;

    const calculateTotal = (field: string) => {
        return summaryList.reduce((prev: any, current: any) => {
            const val = current[field];
            if (val !== '' && val != null && prev != null) {
                return prev + parseFloat(val.toString().replace(',', ''));
            } else if (val === '' || val == null || prev == null) {
                return null;
            } else {
                return prev;
            }
        }, 0);
    };

    return {
        ...currentAccount,
        baseTaxTotal: calculateTotal('baseTax'),
        baseTaxDueTotal: calculateTotal('baseDue'),
        currentMonthTotal: calculateTotal('currentMonthDue'),
        nextMonthTotal: calculateTotal('nextMonthTaxDue'),
    };
}


export function isDuplicateAccountExist(pDay: any, el: any) {
    return (
            trimSpace(pDay.accountNumber) === trimSpace(el.accountNumber) &&
            pDay.subdivisionId === el.subdivisionId &&
            pDay.associationId === el.associationId)
        ||
        (
            trimSpace(pDay.accountNumber) === trimSpace(el.accountNumber) &&
            (
                (pDay.associationId === "" && el.subdivisionId === null) ||
                (pDay.associationId === null && el.subdivisionId === "") ||
                (pDay.subdivisionId === "" && el.subdivisionId === null) ||
                (pDay.associationId === null && el.subdivisionId === "") ||
                (pDay.associationId === "" && el.subdivisionId === "")
            )
        )
}


export function sortSelectedJurisdiction(list: any) {
    return []
        .concat(...list)
        .sort((a: any, b: any) => convertLowerCase(a.name).localeCompare(convertLowerCase(b.name)));
}

export function yearArray(startYear: any, endYear: any) {

    const endDate = endYear;
    let years = [];

    for (let i = startYear; i <= endDate; i++) {
        years.push(startYear);
        startYear++;
    }
    return years;
}

export function getTaxRateYearValue(list: any, taxYear: any) {
    if (list.length > 0) {
        let data = list.filter((el: any) => el.year === taxYear);
        if (data.length > 0) {
            return data[0].rate;
        } else {
            return null;
        }
    }
    return null;
}


export function updateSummarySectionRow(
    certificate: any,
    showAccount: number,
    jurisdictionIdIndex: any,
    taxYear: any,
) {
    const clonedCertificate = jsonCloneWithDates(certificate); // deep clone
    const account = clonedCertificate.accounts[showAccount];
    const jurisdiction = account?.jurisdictionList[jurisdictionIdIndex];

    if (!jurisdiction) {
        console.warn("Invalid jurisdictionIdIndex:", jurisdictionIdIndex);
        return certificate;
    }

    const yearToUse = taxYear || jurisdiction.bill_year;
    const levyPeriod = jurisdiction.levyPeriod;

    if (account.summaryList.length === 1 && account.summaryList[0].taxYear === '') {
        account.summaryList[0] = {
            ...account.summaryList[0],
            taxYear: yearToUse,
            levyPeriod,
            collector: jurisdiction.collectorName,
            collectorAccountNumber: jurisdiction.collectorAccountNumber,
            asOfDate: jurisdiction.asOfDate,
            baseTax: jurisdiction.baseTax,
            baseDue: jurisdiction.baseDue,
            currentMonthDue: jurisdiction.currentMonthDue,
            nextMonthTaxDue: jurisdiction.nextMonthTaxDue,
        };
    } else {
        const alreadyExists = account.summaryList.some(
            (v: any) => v.collector === jurisdiction.collectorName && parseInt(v.taxYear) === parseInt(yearToUse)
        );

        if (!alreadyExists) {
            account.summaryList.push({
                collector: jurisdiction.collectorName,
                collectorAccountNumber: jurisdiction.collectorAccountNumber,
                taxYear: yearToUse,
                baseTax: jurisdiction.baseTax,
                baseDue: jurisdiction.baseDue,
                currentMonthDue: jurisdiction.currentMonthDue,
                nextMonthTaxDue: jurisdiction.nextMonthTaxDue,
                asOfDate: jurisdiction.asOfDate,
            });
        }
    }

    // Cleanup: remove any summary rows not matching any jurisdiction
    account.summaryList = account.summaryList.filter((summary: any) => {
        return account.jurisdictionList.some(
            (jur: any) =>
                jur.collectorName === summary.collector &&
                parseInt(jur.bill_year) === parseInt(summary.taxYear)
        );
    });

    clonedCertificate.accounts[showAccount] = account;
    return clonedCertificate;
}

export function autoCalculateSummaryRow(
    certificate: any,
    showAccount: number
) {
    const clonedCertificate = jsonCloneWithDates(certificate);
    let account = clonedCertificate.accounts[showAccount];
    const {summaryList, jurisdictionList} = account;

    let newSummaryList = [...summaryList]; // start with a copy

    for (let i = 0; i < newSummaryList.length; i++) {
        const matchingJurisdictions = jurisdictionList.filter(
            (jur: any) =>
                jur.collectorName === newSummaryList[i].collector &&
                parseInt(jur.bill_year) === parseInt(newSummaryList[i].taxYear)
        );

        if (matchingJurisdictions.length > 0) {
            newSummaryList = updateSummaryRowInfo(newSummaryList, matchingJurisdictions, i);
        }
    }
    // Update the account with the fully updated list
    account.summaryList = newSummaryList;

    // Update total row if needed
    account = updateSummaryTotalRowCalculation(account, newSummaryList);

    clonedCertificate.accounts[showAccount] = account;
    return clonedCertificate;
}


export const upperCaseFirstLetter = (string: any) =>
    `${string.slice(0, 1).toUpperCase()}${string.slice(1)}`;

export const lowerCaseAllWordsExceptFirstLetters = (string: any) =>
    `${string.slice(0, 1)}${string.slice(1).toLowerCase()}`;

export const feeListHead = (setIsAddNewFee: any, editable: any, curAccount: any) => {
    return (
        <>
            <thead className="bg-highlight-2 text-[white] border-t">
            <tr>
                <th scope="col" className="text-center w-10"><i className="fa fa-plus-circle onHover "
                                                                onClick={() => {
                                                                    if (editable) {
                                                                        curAccount['taxDataSource'] = 'EDITOR';
                                                                        setIsAddNewFee(true);
                                                                    }
                                                                }}
                                                                title={"Add Fee"}/></th>
                <th scope="col" className="text-center"><a
                    className="grid-color toggle-data">Type</a>
                </th>
                <th scope="col" className="text-center"><a
                    className="grid-color toggle-data">Period Start</a>
                </th>
                <th scope="col" className="text-center"><a
                    className="grid-color toggle-data">Period End</a>
                </th>
                <th scope="col" className="text-center"><a
                    className="grid-color toggle-data">Amount</a>
                </th>
                <th scope="col" className="text-center">
                    <a className="grid-color toggle-data">Paid</a>
                </th>
                <th scope="col" className="text-center"><a
                    className="grid-color toggle-data">Current Month</a></th>
                <th scope="col" className="text-center"><a
                    className="grid-color toggle-data">Next Month</a></th>
                <th scope="col" className="text-center"><a
                    className="grid-color toggle-data">Frequency</a>
                </th>
                <th scope="col" className="text-center"><a
                    className="grid-color toggle-data">Note</a>
                </th>
            </tr>
            </thead>
        </>
    )
}

export const selectDropDownField = (
    row: any,
    name: string,
    setIsSavedResult: (val: boolean) => void,
    Enums: any[],
    editable: boolean,
    certificate: any,
    setCertificate: (cert: any) => void,
    showAccount: number,
    setUpdatedAccountIndexList: any
) => {
    const feeListEnabledEnumsRegex = /^(FEE_LIST_ENABLED_TYPE_1|FEE_LIST_ENABLED_TYPE_2)$/; // update this regex as per your actual enums

    return (
        <select
            className="form-control border border-surface-5"
            value={row[name]}
            disabled={!editable}
            onChange={(e) => {
                const newValue = e.target.value;

                const updatedCertificate = {...certificate};
                const targetAccount = {...updatedCertificate.accounts[showAccount]};

                const updatedFees = targetAccount.fees.map((fee: any) => {
                    if (fee === row) {
                        const updatedFee = {...fee, [name]: newValue};

                        if (name === "type" && !feeListEnabledEnumsRegex.test(newValue)) {
                            updatedFee["periodStart"] = "";
                            updatedFee["periodEnd"] = "";
                            updatedFee["frequency"] = "ONE_TIME";
                        }

                        return updatedFee;
                    }
                    return fee;
                });

                targetAccount.fees = updatedFees;
                targetAccount.taxDataSource = "EDITOR";

                updatedCertificate.accounts[showAccount] = targetAccount;
                setCertificate(updatedCertificate);
                setUpdatedAccountIndexList((prev: number[]) =>
                    prev.includes(showAccount) ? prev : [...prev, showAccount]
                );
                setIsSavedResult(false);
            }}
            required={true}
        >
            <option value="">
                {name === "type" ? "Select Type" : "Select Frequency"}
            </option>
            {Enums?.map((curEnum: any) => (
                <option key={curEnum.constant} value={curEnum.constant}>
                    {curEnum.value}
                </option>
            ))}
        </select>
    );
};


export const datePickerField = (
    row: any,
    name: string,
    setIsSavedResult: (val: boolean) => void,
    editable: boolean,
    certificate: any,
    setCertificate: (cert: any) => void,
    showAccount: number,
    setUpdatedAccountIndexList: any
) => {
    const rawDate = row[name];
    const date =
        rawDate != null && rawDate !== ""
            ? new Date(
                new Date(rawDate.toString()).setMinutes(
                    new Date(rawDate.toString()).getMinutes() +
                    new Date(rawDate.toString()).getTimezoneOffset()
                )
            )
            : null;

    // let date = row[name] != null && row[name] !== '' ? new Date(row[name].toString()).setMinutes(new Date(row[name].toString()).getMinutes() + new Date(row[name].toString()).getTimezoneOffset()) : null;
    // let val = date != null ? new Date(date) : null;

    return (
        <DatePicker
            // @ts-ignore
            value={date}
            onChange={(value: any) => {
                const updatedCertificate = {...certificate};
                const targetAccount = {...updatedCertificate.accounts[showAccount]};

                targetAccount.taxDataSource = "EDITOR";

                const updatedFees = targetAccount.fees.map((fee: any) =>
                    fee === row
                        ? {
                            ...fee,
                            [name]:
                                value != null && value !== ""
                                    ? moment(value).format("yyyy-MM-DD").toUpperCase()
                                    : null,
                        }
                        : fee
                );


                setUpdatedAccountIndexList((prev: number[]) =>
                    prev.includes(showAccount) ? prev : [...prev, showAccount]
                );
                targetAccount.fees = updatedFees;
                updatedCertificate.accounts[showAccount] = targetAccount;

                setCertificate(updatedCertificate);
                setIsSavedResult(false);
            }}
            dayPlaceholder={"dd"} monthPlaceholder={"mm"} format={"MM-dd"}
            onKeyDown={(e) => {
                e.preventDefault();
            }}
            disabled={!editable}
        />
    );
};


export const removeDuplicateAndNegativeItems = (arr: any) => {
    arr = arr.filter(function (x: any) {
        return x > -1
    });
    return [...new Set(arr)];
}


export const dummyAssessmentList = () => {
    let currentTaxYear = getCurrentTaxYear();
    let desiredYear = doesCurrentTaxYearMatchCurrentYear() ? currentTaxYear + 1 : currentTaxYear - 1;

    let assessment = {
        "taxYear": desiredYear > currentTaxYear ? currentTaxYear : desiredYear,
        "improvementMarketValue": null,
        "landMarketValue": null,
        "agMarketValue": null,
        "overrideAppraisedValue": null,
        "overrideAssessedValue": null,
        "agTimberValue": null,
        "agLossValue": null,
        "homesteadCapValue": 0,
        "exemptions": [],
        "assessedValue": null,
        "agriculturalValue": null,
        "landTotalValue": null,
        "overrideAgValue": null,
        "appraisedValue": null,
        "certified": false,
        "overrideLandTotalValue": null,
        "woExemptionAmount": null,
        "overrideWoExemptionAmount": null,
        "withExemptionEstimate": null,
        "withExemptionEstimateOverride": null,
    };

    return [
        {
            ...assessment,
            "taxYear": desiredYear > currentTaxYear ? currentTaxYear : desiredYear,
        },
        {
            ...assessment,
            "taxYear": desiredYear > currentTaxYear ? desiredYear : currentTaxYear,
        }
    ]
}


export async function populateStateAndCounty(collectorId: string, isNeedCountyList = false) {
    try {
        const {state, countyName, countyData}: any = await dispatchUtilityRequest("populateStateAndCounty", {
            collectorId,
            isNeedCountyList,
        });
        return {state, countyName, countyData: countyData};
    } catch (error) {
        console.error(error);
        throw error;
    }
}


export const jsonCloneWithDates = <T = any>(obj: T): T => {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }

    const replacer = (key: string, value: any) => {
        if (value instanceof Date) {
            return value.toISOString();
        }
        if (typeof value === 'function') {
            return undefined;
        }
        if (value === undefined) {
            return null;
        }
        return value;
    };

    const reviver = (key: string, value: any) => {
        // Handle Date strings
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
            return new Date(value);
        }
        if (value === null && key === '') {
            return undefined;
        }
        return value;
    };

    try {
        return JSON.parse(JSON.stringify(obj, replacer), reviver);
    } catch (error) {
        console.warn('JSON clone failed, returning original object:', error);
        return obj;
    }
};
